import { useEffect, useId, useState, type ChangeEvent } from 'react';
import {
  api,
  camposDoErro,
  mensagemDoErro,
  type Branding,
  type Canal,
  type Configuracoes as ConfiguracoesApi,
} from '../api';
import { useApp } from '../estado';
import { useRecurso } from '../ganchos';
import { formatarDataHora, horaValida, passaAA, razaoContraste } from '../formatacao';
import { validarArquivoLogo } from '../identidade';
import {
  Aviso,
  Campo,
  Carregando,
  EstadoVazio,
  Indicador,
  Modal,
  Switch,
  TituloPagina,
} from '../componentes/ui';

const FUSOS = [
  { valor: 'America/Sao_Paulo', rotulo: 'Brasília / São Paulo (GMT-3)' },
  { valor: 'America/Manaus', rotulo: 'Manaus (GMT-4)' },
  { valor: 'America/Belem', rotulo: 'Belém (GMT-3)' },
  { valor: 'America/Fortaleza', rotulo: 'Fortaleza (GMT-3)' },
  { valor: 'America/Recife', rotulo: 'Recife (GMT-3)' },
  { valor: 'America/Bahia', rotulo: 'Salvador (GMT-3)' },
  { valor: 'America/Cuiaba', rotulo: 'Cuiabá (GMT-4)' },
  { valor: 'America/Campo_Grande', rotulo: 'Campo Grande (GMT-4)' },
  { valor: 'America/Porto_Velho', rotulo: 'Porto Velho (GMT-4)' },
  { valor: 'America/Rio_Branco', rotulo: 'Rio Branco (GMT-5)' },
  { valor: 'America/Noronha', rotulo: 'Fernando de Noronha (GMT-2)' },
];

interface FormProvedor {
  nome: string;
  fuso: string;
  janelaInicio: string;
  janelaFim: string;
  branding: Branding;
}

interface FormCanal {
  nome: string;
  xmaxQueueId: string;
  apiKey: string;
  numeroOrigem: string;
  vazaoPorMinuto: string;
  concorrencia: string;
}

const CANAL_VAZIO: FormCanal = {
  nome: '',
  xmaxQueueId: '',
  apiKey: '',
  numeroOrigem: '',
  vazaoPorMinuto: '60',
  concorrencia: '4',
};

type EstadoModalCanal = { modo: 'criar' } | { modo: 'editar'; canal: Canal } | null;

function CampoCor({
  rotulo,
  ajuda,
  valor,
  onChange,
}: {
  rotulo: string;
  ajuda?: string;
  valor: string;
  onChange: (cor: string) => void;
}) {
  const id = useId();
  const idAjuda = `${id}-ajuda`;

  return (
    <div className="campo">
      <span className="campo__rotulo" id={id}>
        {rotulo}
      </span>
      <div className="linha" style={{ flexWrap: 'nowrap' }} role="group" aria-labelledby={id}>
        <input
          type="color"
          value={valor}
          onChange={(evento) => onChange(evento.target.value)}
          aria-label={`${rotulo} — seletor de cor`}
          aria-describedby={ajuda ? idAjuda : undefined}
          style={{
            width: '3rem',
            height: '2.75rem',
            padding: '0.15rem',
            borderRadius: '9px',
            border: '1px solid var(--borda-forte)',
            background: 'var(--superficie)',
          }}
        />
        <input
          className="entrada mono"
          value={valor}
          onChange={(evento) => {
            const cor = evento.target.value.trim();
            if (/^#[0-9a-fA-F]{0,6}$/.test(cor)) onChange(cor);
          }}
          aria-label={`${rotulo} — valor hexadecimal`}
          aria-describedby={ajuda ? idAjuda : undefined}
          maxLength={7}
        />
      </div>
      {ajuda ? (
        <span className="campo__ajuda" id={idAjuda}>
          {ajuda}
        </span>
      ) : null}
    </div>
  );
}

export function Configuracoes() {
  const { provedor, notificar, atualizarMarca } = useApp();
  const conf = useRecurso(() => api.configuracoes(), []);
  const [form, setForm] = useState<FormProvedor | null>(null);
  const [errosProvedor, setErrosProvedor] = useState<Record<string, string>>({});
  const [salvandoProvedor, setSalvandoProvedor] = useState(false);

  const [eloForm, setEloForm] = useState<{ baseUrl: string; token: string; modo: 'real' | 'demo' }>({
    baseUrl: '',
    token: '',
    modo: 'demo',
  });
  const [errosElo, setErrosElo] = useState<Record<string, string>>({});
  const [salvandoElo, setSalvandoElo] = useState(false);
  const [testandoElo, setTestandoElo] = useState(false);
  const [resultadoElo, setResultadoElo] = useState<{ sucesso: boolean; detalhe: string } | null>(
    null,
  );

  const [modalCanal, setModalCanal] = useState<EstadoModalCanal>(null);
  const [formCanal, setFormCanal] = useState<FormCanal>(CANAL_VAZIO);
  const [errosCanal, setErrosCanal] = useState<Record<string, string>>({});
  const [salvandoCanal, setSalvandoCanal] = useState(false);
  const [testandoCanal, setTestandoCanal] = useState<string | null>(null);

  const fuso = provedor?.fuso ?? 'America/Sao_Paulo';
  const dados: ConfiguracoesApi | null = conf.dados;

  useEffect(() => {
    if (!dados?.provedor || form) return;
    setForm({
      nome: dados.provedor.nome,
      fuso: dados.provedor.fuso,
      janelaInicio: dados.provedor.janelaInicio,
      janelaFim: dados.provedor.janelaFim,
      branding: { ...dados.provedor.branding },
    });
  }, [dados?.provedor, form]);

  useEffect(() => {
    if (!dados?.elo) return;
    setEloForm((atual) =>
      atual.baseUrl === '' && atual.token === ''
        ? { baseUrl: dados.elo!.baseUrl, token: '', modo: dados.elo!.modo }
        : atual,
    );
  }, [dados?.elo]);

  const atualizarBranding = (mudanca: Partial<Branding>) => {
    setForm((atual) =>
      atual ? { ...atual, branding: { ...atual.branding, ...mudanca } } : atual,
    );
  };

  const selecionarLogo = async (evento: ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.currentTarget.files?.[0];
    evento.currentTarget.value = '';
    if (!arquivo) return;

    const erro = validarArquivoLogo(arquivo);
    if (erro) {
      setErrosProvedor((atual) => ({ ...atual, logoUrl: erro }));
      return;
    }

    try {
      const logoUrl = await new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => {
          if (typeof leitor.result === 'string') resolve(leitor.result);
          else reject(new Error('O arquivo selecionado não pôde ser lido como imagem.'));
        };
        leitor.onerror = () => reject(new Error('Não foi possível ler a imagem selecionada.'));
        leitor.readAsDataURL(arquivo);
      });
      atualizarBranding({ logoUrl });
      setErrosProvedor((atual) => {
        const restante = { ...atual };
        delete restante.logoUrl;
        return restante;
      });
    } catch (falha) {
      setErrosProvedor((atual) => ({
        ...atual,
        logoUrl: falha instanceof Error ? falha.message : 'Não foi possível ler a imagem.',
      }));
    }
  };

  const salvarProvedor = async () => {
    if (!form) return;
    const locais: Record<string, string> = {};
    if (form.nome.trim().length < 2) locais.nome = 'Informe o nome do Provedor.';
    if (!horaValida(form.janelaInicio)) locais.janelaInicio = 'Horário inválido (HH:MM).';
    if (!horaValida(form.janelaFim)) locais.janelaFim = 'Horário inválido (HH:MM).';
    if (form.janelaInicio >= form.janelaFim) {
      locais.janelaFim = 'O fim precisa ser depois do início.';
    }
    if (!passaAA(form.branding.corPrimaria, '#ffffff')) {
      locais['branding.corPrimaria'] =
        'Essa cor não passa AA com texto branco (mínimo 4,5:1). Escolha um tom mais escuro.';
    }
    if (Object.keys(locais).length > 0) {
      setErrosProvedor(locais);
      return;
    }

    setSalvandoProvedor(true);
    try {
      const branding = {
        ...form.branding,
        nomeExibicao: form.branding.nomeExibicao.trim(),
        logoUrl: form.branding.logoUrl?.trim() ? form.branding.logoUrl.trim() : null,
      };
      await api.salvarProvedor({
        nome: form.nome.trim(),
        fuso: form.fuso,
        janelaInicio: form.janelaInicio,
        janelaFim: form.janelaFim,
        branding,
      });
      atualizarMarca(branding);
      setErrosProvedor({});
      notificar('sucesso', 'Dados do Provedor salvos.');
      await conf.recarregar();
    } catch (falha) {
      const campos = camposDoErro(falha);
      if (Object.keys(campos).length > 0) setErrosProvedor(campos);
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setSalvandoProvedor(false);
    }
  };

  const salvarElo = async () => {
    const locais: Record<string, string> = {};
    if (!/^https?:\/\/.+/.test(eloForm.baseUrl)) {
      locais.baseUrl = 'Informe o endereço completo da conexão (https://…).';
    }
    if (!dados?.elo && eloForm.token.trim().length < 8) {
      locais.token = 'Informe o Token Opaco do Provedor.';
    }
    if (Object.keys(locais).length > 0) {
      setErrosElo(locais);
      return;
    }

    setSalvandoElo(true);
    try {
      await api.salvarElo({
        baseUrl: eloForm.baseUrl.trim(),
        modo: eloForm.modo,
        ...(eloForm.token.trim() ? { token: eloForm.token.trim() } : {}),
      });
      setErrosElo({});
      setEloForm((atual) => ({ ...atual, token: '' }));
      notificar('sucesso', 'Conexão com o ERP salva.');
      await conf.recarregar();
    } catch (falha) {
      const campos = camposDoErro(falha);
      if (Object.keys(campos).length > 0) setErrosElo(campos);
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setSalvandoElo(false);
    }
  };

  const testarElo = async () => {
    setTestandoElo(true);
    try {
      const resultado = await api.testarElo();
      setResultadoElo(resultado);
      notificar(resultado.sucesso ? 'sucesso' : 'erro', resultado.detalhe);
      await conf.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setTestandoElo(false);
    }
  };

  const abrirCriacaoCanal = () => {
    setFormCanal(CANAL_VAZIO);
    setErrosCanal({});
    setModalCanal({ modo: 'criar' });
  };

  const abrirEdicaoCanal = (canal: Canal) => {
    setFormCanal({
      nome: canal.nome,
      xmaxQueueId: String(canal.xmaxQueueId),
      apiKey: '',
      numeroOrigem: canal.numeroOrigem,
      vazaoPorMinuto: String(canal.vazaoPorMinuto),
      concorrencia: String(canal.concorrencia),
    });
    setErrosCanal({});
    setModalCanal({ modo: 'editar', canal });
  };

  const salvarCanal = async () => {
    const locais: Record<string, string> = {};
    if (formCanal.nome.trim().length < 2) locais.nome = 'Dê um nome ao canal.';
    if (!/^\d+$/.test(formCanal.xmaxQueueId) || Number(formCanal.xmaxQueueId) < 1) {
      locais.xmaxQueueId = 'Informe o número da fila oficial.';
    }
    if (modalCanal?.modo === 'criar' && formCanal.apiKey.trim().length < 4) {
      locais.apiKey = 'Informe a apiKey da fila (XMAX).';
    }
    const vazao = Number(formCanal.vazaoPorMinuto);
    if (!Number.isInteger(vazao) || vazao < 1 || vazao > 6000) {
      locais.vazaoPorMinuto = 'Entre 1 e 6000 mensagens por minuto.';
    }
    const concorrencia = Number(formCanal.concorrencia);
    if (!Number.isInteger(concorrencia) || concorrencia < 1 || concorrencia > 50) {
      locais.concorrencia = 'Entre 1 e 50 envios simultâneos.';
    }
    if (Object.keys(locais).length > 0) {
      setErrosCanal(locais);
      return;
    }

    setSalvandoCanal(true);
    try {
      const dadosCanal = {
        nome: formCanal.nome.trim(),
        xmaxQueueId: Number(formCanal.xmaxQueueId),
        numeroOrigem: formCanal.numeroOrigem.trim(),
        vazaoPorMinuto: vazao,
        concorrencia,
        ...(formCanal.apiKey.trim() ? { apiKey: formCanal.apiKey.trim() } : {}),
      };
      if (modalCanal?.modo === 'editar') {
        await api.atualizarCanal(modalCanal.canal.id, dadosCanal);
        notificar('sucesso', 'Canal atualizado.');
      } else {
        await api.criarCanal({
          ...dadosCanal,
          apiKey: formCanal.apiKey.trim(),
        });
        notificar('sucesso', 'Canal criado.');
      }
      setModalCanal(null);
      await conf.recarregar();
    } catch (falha) {
      const campos = camposDoErro(falha);
      if (Object.keys(campos).length > 0) setErrosCanal(campos);
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setSalvandoCanal(false);
    }
  };

  const alternarCanal = async (canal: Canal, ativo: boolean) => {
    try {
      await api.atualizarCanal(canal.id, { ativo });
      notificar('info', ativo ? `Canal ${canal.nome} ativado.` : `Canal ${canal.nome} desativado.`);
      await conf.recarregar();
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    }
  };

  const testarCanal = async (canal: Canal) => {
    setTestandoCanal(canal.id);
    try {
      const resultado = await api.testarCanal(canal.id);
      notificar(resultado.sucesso ? 'sucesso' : 'erro', resultado.detalhe);
    } catch (falha) {
      notificar('erro', mensagemDoErro(falha));
    } finally {
      setTestandoCanal(null);
    }
  };

  if (conf.carregando && !dados) return <Carregando texto="Lendo as configurações do Provedor…" />;

  if (conf.erro && !dados) {
    return (
      <Aviso tipo="erro" titulo="Não foi possível carregar as configurações">
        {conf.erro}
      </Aviso>
    );
  }

  const razao = form ? razaoContraste(form.branding.corPrimaria, '#ffffff') : 0;

  return (
    <>
      <TituloPagina
        titulo="Configurações"
        descricao="Identidade do Provedor, janela de envio e integrações. A marca é white-label; a fila e o Token Opaco são sempre deste Provedor."
      />

      {form ? (
        <>
          <section className="cartao" aria-labelledby="provedor-titulo">
            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h2 id="provedor-titulo">Provedor</h2>
                <p className="cartao__descricao">
                  O fuso e a janela valem para todos os Disparos. Fora da janela, a mensagem é
                  reprogramada para o próximo horário válido.
                </p>
              </div>
              <span className="badge badge--neutro mono">{dados?.provedor?.slug}</span>
            </div>

            <div className="grade grade--2">
              <Campo rotulo="Nome do Provedor" erro={errosProvedor.nome}>
                <input
                  className="entrada"
                  value={form.nome}
                  onChange={(evento) => setForm({ ...form, nome: evento.target.value })}
                />
              </Campo>
              <Campo
                rotulo="Fuso horário"
                ajuda="Toda data civil, janela e agendamento usa este fuso."
              >
                <select
                  className="entrada"
                  value={form.fuso}
                  onChange={(evento) => setForm({ ...form, fuso: evento.target.value })}
                >
                  {FUSOS.map((opcao) => (
                    <option key={opcao.valor} value={opcao.valor}>
                      {opcao.rotulo}
                    </option>
                  ))}
                </select>
              </Campo>
              <Campo
                rotulo="Janela de Envio — início"
                ajuda="Antes deste horário nada sai."
                erro={errosProvedor.janelaInicio}
              >
                <input
                  className="entrada"
                  type="time"
                  value={form.janelaInicio}
                  onChange={(evento) => setForm({ ...form, janelaInicio: evento.target.value })}
                />
              </Campo>
              <Campo
                rotulo="Janela de Envio — fim"
                ajuda="Depois deste horário o Disparo espera o dia seguinte."
                erro={errosProvedor.janelaFim}
              >
                <input
                  className="entrada"
                  type="time"
                  value={form.janelaFim}
                  onChange={(evento) => setForm({ ...form, janelaFim: evento.target.value })}
                />
              </Campo>
            </div>

            <hr className="separador" style={{ margin: 'var(--e5) 0' }} />

            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h3>Identidade visual</h3>
                <p className="cartao__descricao">
                  As cores e a logo aparecem no painel após salvar. A cor primária precisa passar
                  AA com texto branco para manter a legibilidade dos botões.
                </p>
              </div>
            </div>

            <div className="grade grade--2">
              <Campo rotulo="Nome de exibição" ajuda="Como o Provedor aparece ao Titular.">
                <input
                  className="entrada"
                  value={form.branding.nomeExibicao}
                  onChange={(evento) => atualizarBranding({ nomeExibicao: evento.target.value })}
                />
              </Campo>
              <Campo rotulo="Contato de suporte" ajuda="Aparece no rodapé das mensagens.">
                <input
                  className="entrada"
                  value={form.branding.suporte}
                  onChange={(evento) => atualizarBranding({ suporte: evento.target.value })}
                  placeholder="(00) 0000-0000"
                />
              </Campo>
              <CampoCor
                rotulo="Cor primária"
                ajuda="Usada em botões e destaques."
                valor={form.branding.corPrimaria}
                onChange={(cor) => atualizarBranding({ corPrimaria: cor })}
              />
              <CampoCor
                rotulo="Cor de acento"
                ajuda="Só decoração: ícones, barra e gradiente."
                valor={form.branding.corAcento}
                onChange={(cor) => atualizarBranding({ corAcento: cor })}
              />
              <Campo
                rotulo="URL externa do logo"
                opcional
                ajuda="Alternativa ao envio do dispositivo; deixe vazio para não usar."
              >
                <input
                  className="entrada"
                  value={
                    form.branding.logoUrl?.startsWith('data:image/')
                      ? ''
                      : form.branding.logoUrl ?? ''
                  }
                  onChange={(evento) => atualizarBranding({ logoUrl: evento.target.value })}
                  placeholder="https://…/logo.png"
                  aria-describedby={errosProvedor.logoUrl ? 'erro-logo' : undefined}
                  aria-invalid={Boolean(errosProvedor.logoUrl)}
                />
              </Campo>
              <Campo
                rotulo="Enviar logo do dispositivo"
                opcional
                ajuda="PNG, JPG ou WebP, até 400 KiB."
              >
                <input
                  className="entrada"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(evento) => void selecionarLogo(evento)}
                  aria-describedby={errosProvedor.logoUrl ? 'erro-logo' : undefined}
                  aria-invalid={Boolean(errosProvedor.logoUrl)}
                />
              </Campo>
            </div>

            {errosProvedor.logoUrl ? (
              <div id="erro-logo" role="alert" className="campo__erro">
                {errosProvedor.logoUrl}
              </div>
            ) : null}
            {form.branding.logoUrl ? (
              <div
                className="linha"
                style={{ marginTop: '1rem', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <img
                  src={form.branding.logoUrl}
                  alt="Prévia da logo do Provedor"
                  style={{
                    width: '10rem',
                    height: '4rem',
                    objectFit: 'contain',
                    border: '1px solid var(--borda)',
                    borderRadius: 'var(--raio-sm)',
                    padding: '0.4rem',
                  }}
                />
                <button
                  type="button"
                  className="botao botao--secundario botao--pequeno"
                  onClick={() => atualizarBranding({ logoUrl: null })}
                >
                  Remover logo
                </button>
              </div>
            ) : null}

            {errosProvedor['branding.corPrimaria'] ? (
              <div style={{ marginTop: '1rem' }}>
                <Aviso tipo="erro" titulo="Contraste reprovado">
                  {errosProvedor['branding.corPrimaria']}
                </Aviso>
              </div>
            ) : razao < 4.5 ? (
              <div style={{ marginTop: '1rem' }}>
                <Aviso tipo="alerta" titulo={`Contraste ${razao.toFixed(2)}:1 com branco`}>
                  Abaixo de 4,5:1 o texto do botão fica ilegível para parte dos Atendentes. O
                  servidor recusa essa cor.
                </Aviso>
              </div>
            ) : (
              <div style={{ marginTop: '1rem' }}>
                <Aviso tipo="ok" titulo={`Contraste ${razao.toFixed(2)}:1 com branco`}>
                  Aprovado em AA para texto normal.
                </Aviso>
              </div>
            )}

            <div className="linha" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="botao botao--primario"
                onClick={() => void salvarProvedor()}
                disabled={salvandoProvedor}
              >
                {salvandoProvedor ? 'Salvando…' : 'Salvar Provedor'}
              </button>
            </div>
          </section>

          <section className="cartao" aria-labelledby="canais-titulo">
            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h2 id="canais-titulo">Canais de saída</h2>
                <p className="cartao__descricao">
                  Cada canal é uma fila oficial da XMAX com apiKey própria. O motor nunca fala com
                  a Cloud API da Meta.
                </p>
              </div>
              <button type="button" className="botao botao--secundario" onClick={abrirCriacaoCanal}>
                Novo canal
              </button>
            </div>

            {dados && dados.canais.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum canal cadastrado"
                texto="Sem canal ativo, os Disparos ficam na fila como pendentes. Cadastre a fila para começar a enviar."
                icone="↗"
                acao={
                  <button type="button" className="botao botao--primario" onClick={abrirCriacaoCanal}>
                    Cadastrar canal
                  </button>
                }
              />
            ) : (
              <div className="tabela-rolagem">
                <table>
                  <caption className="sr-apenas">Canais do Provedor</caption>
                  <thead>
                    <tr>
                      <th scope="col">Canal</th>
                      <th scope="col">Fila</th>
                      <th scope="col">Vazão</th>
                      <th scope="col">Concorrência</th>
                      <th scope="col">Situação</th>
                      <th scope="col">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados?.canais.map((canal) => (
                      <tr key={canal.id}>
                        <td>
                          <strong>{canal.nome}</strong>
                          <br />
                          <span className="texto-pequeno">
                            {canal.numeroOrigem || 'Sem número de origem'}
                          </span>
                        </td>
                        <td className="mono">#{canal.xmaxQueueId}</td>
                        <td className="numero">{canal.vazaoPorMinuto} msg/min</td>
                        <td className="numero">{canal.concorrencia}</td>
                        <td>
                          <Switch
                            rotulo={canal.ativo ? 'Ativo' : 'Inativo'}
                            checked={canal.ativo}
                            onChange={(valor) => void alternarCanal(canal, valor)}
                          />
                        </td>
                        <td>
                          <div className="acoes-tabela">
                            <button
                              type="button"
                              className="botao botao--secundario botao--pequeno"
                              onClick={() => abrirEdicaoCanal(canal)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="botao botao--secundario botao--pequeno"
                              onClick={() => void testarCanal(canal)}
                              disabled={testandoCanal === canal.id}
                            >
                              {testandoCanal === canal.id ? 'Testando…' : 'Testar'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="cartao" aria-labelledby="elo-titulo">
            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h2 id="elo-titulo">Conexão com o ERP</h2>
                <p className="cartao__descricao">
                  O motor lê faturas e pede desbloqueio pela conexão oficial do Provedor, sem
                  acessar o banco do ERP. O acesso usa um token emitido para este Provedor, guardado
                  cifrado aqui.
                </p>
              </div>
              {dados?.elo ? (
                <Indicador
                  estado={
                    dados.elo.modo === 'demo'
                      ? 'alerta'
                      : dados.elo.ultimoTesteOk === false
                        ? 'erro'
                        : dados.elo.ultimoTesteOk
                          ? 'ok'
                          : 'neutro'
                  }
                >
                  {dados.elo.modo === 'demo'
                    ? 'Demonstração'
                    : dados.elo.ultimoTesteOk === false
                      ? 'Teste falhou'
                      : dados.elo.ultimoTesteOk
                        ? 'Testado com sucesso'
                        : 'Ainda não testado'}
                </Indicador>
              ) : null}
            </div>

            <div className="grade grade--2">
              <Campo rotulo="Endereço da conexão" erro={errosElo.baseUrl}>
                <input
                  className="entrada"
                  value={eloForm.baseUrl}
                  onChange={(evento) => setEloForm({ ...eloForm, baseUrl: evento.target.value })}
                  placeholder="https://conexao.do.provedor.com.br"
                />
              </Campo>
              <Campo
                rotulo="Token de acesso do Provedor"
                opcional={Boolean(dados?.elo)}
                ajuda={
                  dados?.elo
                    ? 'Deixe vazio para manter o token atual. O segredo é cifrado no banco.'
                    : 'Token emitido pelo gateway do ERP para este Provedor.'
                }
                erro={errosElo.token}
              >
                <input
                  className="entrada"
                  type="password"
                  autoComplete="off"
                  value={eloForm.token}
                  onChange={(evento) => setEloForm({ ...eloForm, token: evento.target.value })}
                  placeholder={dados?.elo ? '••••••••••••' : 'cole o token aqui'}
                />
              </Campo>
              <Campo
                rotulo="Modo"
                ajuda="Demonstração simula o ERP e não altera nenhuma conexão."
              >
                <select
                  className="entrada"
                  value={eloForm.modo}
                  onChange={(evento) =>
                    setEloForm({ ...eloForm, modo: evento.target.value as 'real' | 'demo' })
                  }
                >
                  <option value="demo">Demonstração (simulado)</option>
                  <option value="real">Real (conectado ao ERP)</option>
                </select>
              </Campo>
            </div>

            <div className="linha" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="botao botao--primario"
                onClick={() => void salvarElo()}
                disabled={salvandoElo}
              >
                {salvandoElo ? 'Salvando…' : 'Salvar conexão'}
              </button>
              <button
                type="button"
                className="botao botao--secundario"
                onClick={() => void testarElo()}
                disabled={testandoElo}
              >
                {testandoElo ? 'Testando…' : 'Testar conexão'}
              </button>
              {dados?.elo?.ultimoTesteEm ? (
                <span className="texto-pequeno">
                  Último teste: {formatarDataHora(dados.elo.ultimoTesteEm, fuso)}
                </span>
              ) : null}
            </div>

            {resultadoElo ? (
              <div style={{ marginTop: '1rem' }}>
                <Aviso tipo={resultadoElo.sucesso ? 'ok' : 'erro'} titulo="Resultado do teste">
                  {resultadoElo.detalhe}
                </Aviso>
              </div>
            ) : null}
          </section>

          <section className="cartao cartao--compacto" aria-labelledby="ambiente-titulo">
            <div className="cartao__cabecalho">
              <div className="cartao__titulo">
                <h2 id="ambiente-titulo">Ambiente</h2>
              </div>
            </div>
            <dl className="detalhe">
              <div>
                <dt>Modo do canal (XMAX)</dt>
                <dd>
                  <StatusBadgeAmbiente modo={dados?.ambiente.xmaxModo ?? 'demo'} />
                </dd>
              </div>
              <div>
                <dt>Endereço padrão da conexão</dt>
                <dd className="mono">{dados?.ambiente.eloUrlPadrao || '—'}</dd>
              </div>
              <div>
                <dt>Fuso do Provedor</dt>
                <dd>{fuso}</dd>
              </div>
            </dl>
          </section>
        </>
      ) : null}

      <Modal
        aberto={modalCanal !== null}
        titulo={modalCanal?.modo === 'editar' ? 'Editar canal' : 'Novo canal'}
        descricao="Os dados vêm do painel da XMAX para este Provedor. A apiKey é cifrada antes de tocar o banco."
        onFechar={() => (salvandoCanal ? undefined : setModalCanal(null))}
        rodape={
          <>
            <button
              type="button"
              className="botao botao--secundario"
              onClick={() => setModalCanal(null)}
              disabled={salvandoCanal}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="botao botao--primario"
              onClick={() => void salvarCanal()}
              disabled={salvandoCanal}
            >
              {salvandoCanal ? 'Salvando…' : 'Salvar canal'}
            </button>
          </>
        }
      >
        <div className="grade grade--2">
          <Campo rotulo="Nome do canal" erro={errosCanal.nome}>
            <input
              className="entrada"
              value={formCanal.nome}
              onChange={(evento) => setFormCanal({ ...formCanal, nome: evento.target.value })}
              placeholder="WhatsApp principal"
            />
          </Campo>
          <Campo
            rotulo="ID da fila (XMAX)"
            ajuda="A fila oficial daquele Provedor."
            erro={errosCanal.xmaxQueueId}
          >
            <input
              className="entrada"
              inputMode="numeric"
              value={formCanal.xmaxQueueId}
              onChange={(evento) =>
                setFormCanal({ ...formCanal, xmaxQueueId: evento.target.value.replace(/\D/g, '') })
              }
              placeholder="42"
            />
          </Campo>
          <Campo
            rotulo="apiKey da fila"
            opcional={modalCanal?.modo === 'editar'}
            ajuda={
              modalCanal?.modo === 'editar'
                ? 'Deixe vazio para manter a apiKey atual.'
                : 'Copie do painel da XMAX. Não é a chave da Meta.'
            }
            erro={errosCanal.apiKey}
          >
            <input
              className="entrada"
              type="password"
              autoComplete="off"
              value={formCanal.apiKey}
              onChange={(evento) => setFormCanal({ ...formCanal, apiKey: evento.target.value })}
              placeholder={modalCanal?.modo === 'editar' ? '••••••••' : 'xmax_…'}
            />
          </Campo>
          <Campo rotulo="Número de origem" opcional ajuda="Número que aparece para o Titular.">
            <input
              className="entrada"
              value={formCanal.numeroOrigem}
              onChange={(evento) =>
                setFormCanal({ ...formCanal, numeroOrigem: evento.target.value })
              }
              placeholder="5519999999999"
            />
          </Campo>
          <Campo
            rotulo="Vazão por minuto"
            ajuda="Limite do canal. O worker nunca passa disso."
            erro={errosCanal.vazaoPorMinuto}
          >
            <input
              className="entrada"
              inputMode="numeric"
              value={formCanal.vazaoPorMinuto}
              onChange={(evento) =>
                setFormCanal({
                  ...formCanal,
                  vazaoPorMinuto: evento.target.value.replace(/\D/g, ''),
                })
              }
            />
          </Campo>
          <Campo
            rotulo="Concorrência"
            ajuda="Envios simultâneos neste canal."
            erro={errosCanal.concorrencia}
          >
            <input
              className="entrada"
              inputMode="numeric"
              value={formCanal.concorrencia}
              onChange={(evento) =>
                setFormCanal({
                  ...formCanal,
                  concorrencia: evento.target.value.replace(/\D/g, ''),
                })
              }
            />
          </Campo>
        </div>
        <Aviso tipo="info" titulo="Fila por Provedor">
          A vazão e a concorrência são deste canal. Um Provedor com 30 mil faturas não consome a cota
          de outro.
        </Aviso>
      </Modal>
    </>
  );
}

function StatusBadgeAmbiente({ modo }: { modo: 'real' | 'demo' }) {
  return modo === 'real' ? (
    <span className="badge badge--ok">
      <span aria-hidden="true">✓</span> Real
    </span>
  ) : (
    <span className="badge badge--alerta">
      <span aria-hidden="true">⚠</span> Demonstração
    </span>
  );
}
